import { AuctionsNearMeRouteParams } from '../types/AuctionsNearMe.types';
import { createBoundingBoxString } from './createBoundingBoxString';
import { fetchCatalogsNearMeByBounds } from '@/redux/modules/auctionsNearMe/auctionsNearMe.actions';
import { GeoArea } from '@/types/GeoArea';
import { geocodeLocation } from './geocodeLocation.api';
import { MapBounds } from '../components/MapView/components/GoogleMap/AuctionsNearMeGoogleMap';
import { useAppDispatch } from '@/redux/hooks';
import { useEffect } from 'react';

export type UseGeocodeCustomRelatedGeoAreaParams = {
    currentActiveBoundsRef: { current: MapBounds | null };
    currentPage: number;
    customSearchBounds: MapBounds | null;
    geoArea: GeoArea | undefined;
    googleMapsReady: boolean;
    hasNewUrlParams: boolean;
    pageSize: number;
    routeParams: AuctionsNearMeRouteParams;
    setCustomSearchBounds: (bounds: MapBounds | null) => void;
    setGeocoderFailed: (failed: boolean) => void;
    setIsGeocoding: (isGeocoding: boolean) => void;
    setSearchTerm: (term: string) => void;
    // TODO: Remove feature flag once new auctions near me page is fully deployed
    // @see https://liveauctioneers.atlassian.net/browse/SG-229
    useNewAuctionsNearMePage: boolean;
};

/**
 * Custom hook to geocode city/state from URL params when NOT in predefined geoAreas list
 * This handles RelatedGeoAreas ("More Locations" in the UI) links to custom locations
 * (e.g., /find-auctions-near-me/ny/patchogue where Patchogue is not in our geoArea list)
 */
export const useGeocodeCustomRelatedGeoArea = ({
    currentActiveBoundsRef,
    currentPage,
    customSearchBounds,
    geoArea,
    googleMapsReady,
    hasNewUrlParams,
    pageSize,
    routeParams,
    setCustomSearchBounds,
    setGeocoderFailed,
    setIsGeocoding,
    setSearchTerm,
    useNewAuctionsNearMePage,
}: UseGeocodeCustomRelatedGeoAreaParams) => {
    const dispatch = useAppDispatch();

    useEffect(() => {
        if (!useNewAuctionsNearMePage || !hasNewUrlParams || geoArea || !googleMapsReady || customSearchBounds) {
            return;
        }

        // Extract city and state from route params (type guard for new URL format)
        const city = 'city' in routeParams ? routeParams.city : undefined;
        const state = 'state' in routeParams ? routeParams.state : undefined;

        if (!city || !state) {
            return;
        }

        const google = typeof window !== 'undefined' ? window.google : null;

        if (!google) {
            return;
        }

        const handleCustomGeoArea = async () => {
            setIsGeocoding(true);

            // Construct search string from city and state (e.g., "Patchogue, NY")
            const locationString = `${city}, ${state}`;

            const bounds = await geocodeLocation(google, locationString);

            if (bounds) {
                // Geocoding succeeded
                setGeocoderFailed(false);
                setSearchTerm(locationString);
                setCustomSearchBounds(bounds);
                // Store bounds in ref for pagination to use
                currentActiveBoundsRef.current = bounds;

                // Fetch catalogs for the geocoded location
                const insideBoundingBox = createBoundingBoxString(bounds);
                dispatch(
                    fetchCatalogsNearMeByBounds({
                        insideBoundingBox,
                        page: currentPage,
                        pageSize: pageSize,
                    })
                );
            } else {
                // Geocoding failed
                setGeocoderFailed(true);
                setSearchTerm(locationString);
            }

            setIsGeocoding(false);
        };

        handleCustomGeoArea();
    }, [
        useNewAuctionsNearMePage,
        hasNewUrlParams,
        geoArea,
        googleMapsReady,
        customSearchBounds,
        routeParams,
        currentPage,
        pageSize,
        dispatch,
        setCustomSearchBounds,
        setGeocoderFailed,
        setIsGeocoding,
        setSearchTerm,
        currentActiveBoundsRef,
    ]);
};
